import { useState, useCallback } from 'react'
import { createShareLink } from '../services/api'

export type ShareLinkState = 'idle' | 'creating' | 'copied' | 'manual' | 'error'

interface UseShareLinkReturn {
  state: ShareLinkState
  shareUrl: string | null
  errorMessage: string | null
  copyShareLink: (sourceUrl: string) => Promise<void>
  reset: () => void
}

/**
 * Starts the clipboard write immediately, before the link resolves.
 *
 * Safari revokes clipboard permission once the user-activation window closes, and an awaited
 * network call reliably outlives it — so awaiting the link and then calling writeText() fails
 * exactly where this app is most used. Handing ClipboardItem a promise keeps the write inside
 * the originating gesture. Browsers without that form fall back to await-then-write.
 *
 * Never rejects: resolves false when the clipboard refuses, so the caller can show the link
 * for manual copying instead of reporting a failure that didn't happen.
 */
function beginClipboardWrite(textPromise: Promise<string>): Promise<boolean> {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    const blob = textPromise.then((text) => new Blob([text], { type: 'text/plain' }))
    return navigator.clipboard
      .write([new ClipboardItem({ 'text/plain': blob })])
      .then(() => true)
      .catch(() => false)
  }

  return textPromise
    .then((text) => navigator.clipboard.writeText(text))
    .then(() => true)
    .catch(() => false)
}

export function useShareLink(): UseShareLinkReturn {
  const [state, setState] = useState<ShareLinkState>('idle')
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const reset = useCallback(() => {
    setState('idle')
    setShareUrl(null)
    setErrorMessage(null)
  }, [])

  const copyShareLink = useCallback(async (sourceUrl: string) => {
    setState('creating')
    setShareUrl(null)
    setErrorMessage(null)

    const linkPromise = createShareLink(sourceUrl)
    const clipboardPromise = beginClipboardWrite(linkPromise)

    try {
      const url = await linkPromise
      setShareUrl(url)
      setState((await clipboardPromise) ? 'copied' : 'manual')
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Could not create share link')
      setState('error')
    }
  }, [])

  return { state, shareUrl, errorMessage, copyShareLink, reset }
}
